/**
 * PolicyTableRow
 * Shared row components for policy tables
 */

import React from 'react';
import { Button, Text, Checkbox } from '@fluentui/react-components';
import { Eye20Regular, Play20Regular } from '@fluentui/react-icons';
import { StatusBadge, StatusType } from '../../StatusBadge';
import ExpandToggle from '../../common/ExpandToggle';
import { FormattedDateTime } from '../../common/DateTimeWithUTCInfo';
import { CoveragePopover } from './CoveragePopover';
import { DashboardPolicy, TenantPolicy, TenantDeployment } from '../types';
import { COLORS } from '../constants';

// Helper to get accessible priority colors (WCAG AA compliant)
const getPriorityColor = (priority: string | number): string => {
  const lower = String(priority).toLowerCase();
  if (lower === 'high') return COLORS.error; // Red
  if (lower === 'medium') return COLORS.warning; // Accessible orange
  if (lower === 'low') return COLORS.success; // Green
  return 'inherit';
};

// Common row style generator
export const getRowStyle = (gridColumns: string, isLastRow: boolean, isSelected?: boolean, hasExpanded?: boolean) => ({
  display: 'grid',
  gridTemplateColumns: gridColumns,
  alignItems: 'center',
  padding: '16px 20px',
  fontSize: 14,
  borderBottom: isLastRow && !hasExpanded ? 'none' : '1px solid #f0f0f0',
  background: isSelected ? '#f0f7ff' : 'white',
});

interface DashboardPolicyRowProps {
  policy: DashboardPolicy;
  gridColumns: string;
  isExpanded: boolean;
  isLastRow: boolean;
  showPriority?: boolean;
  getTenantNameById: (tenantId: string) => string;
  onToggleExpand: () => void;
  onViewDetails: () => void;
  onStartDeployment: () => void;
}

export function DashboardPolicyRow({
  policy,
  gridColumns,
  isExpanded,
  isLastRow,
  showPriority = true,
  getTenantNameById,
  onToggleExpand,
  onViewDetails,
  onStartDeployment,
}: DashboardPolicyRowProps) {
  return (
    <div style={getRowStyle(gridColumns, isLastRow, false, isExpanded)}>
      <div>
        <ExpandToggle
          isOpen={isExpanded}
          onClick={onToggleExpand}
          ariaLabel={isExpanded ? 'Collapse row' : 'Expand row'}
        />
      </div>
      <div style={{ fontWeight: 600 }}>{policy.policyName}</div>
      {showPriority && (
        <div>
          <Text style={{ fontSize: 13, fontWeight: 600, color: getPriorityColor(policy.priority) }}>
            {policy.priority}
          </Text>
        </div>
      )}
      <div>
        <CoveragePopover
          deployedCount={policy.deployedTenantsCount}
          totalCount={policy.totalTenantsCount}
          tenantDeployments={policy.tenantDeployments}
          getTenantNameById={getTenantNameById}
        />
      </div>
      <div>
        <FormattedDateTime dateTime={policy.lastModified} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
        <Button
          appearance="primary"
          size="small"
          icon={<Play20Regular />}
          onClick={onStartDeployment}
          title="Compare and deploy policy to tenants"
        >
          Compare and Deploy
        </Button>
        <Button
          appearance="secondary"
          size="small"
          onClick={onViewDetails}
          icon={<Eye20Regular />}
        >
          View Details
        </Button>
      </div>
    </div>
  );
}

interface TenantPolicyRowProps {
  policy: TenantPolicy;
  gridColumns: string;
  isSelected: boolean;
  isLastRow: boolean;
  showPriority?: boolean;
  status: StatusType;
  onToggleSelect: (checked: boolean) => void;
  onViewDetails: () => void;
}

export function TenantPolicyRow({
  policy,
  gridColumns,
  isSelected,
  isLastRow,
  showPriority = true,
  status,
  onToggleSelect,
  onViewDetails,
}: TenantPolicyRowProps) {
  return (
    <div style={getRowStyle(gridColumns, isLastRow, isSelected)}>
      <div>
        <Checkbox
          checked={isSelected}
          onChange={(_, data) => onToggleSelect(!!data.checked)}
          aria-label={`Select policy ${policy.policyName}`}
          input={{ 'aria-label': `Select policy ${policy.policyName}` }}
        />
      </div>
      <div style={{ fontWeight: 600 }}>{policy.policyName}</div>
      {showPriority && (
        <div>
          <Text style={{ fontSize: 13, fontWeight: 600, color: getPriorityColor(policy.priority) }}>
            {policy.priority}
          </Text>
        </div>
      )}
      <div>
        <StatusBadge status={status} />
      </div>
      <div>
        {policy.lastModified ? (
          <FormattedDateTime dateTime={policy.lastModified} />
        ) : (
          <span style={{ color: '#6b7280' }}>—</span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
        <Button
          appearance="secondary"
          size="small"
          icon={<Eye20Regular />}
          onClick={onViewDetails}
        >
          View Details
        </Button>
      </div>
    </div>
  );
}
